import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

const REWARDS = gql`
  query AdminReferralReviewList($userId: String!) {
    adminReferralRewards(userId: $userId) {
      id friendId friendName friendEmail invoiceId friendSubscriptionId activatedAt eligibleAt status
      reviewedBy reviewedAt reviewNote issue targetBillingAt originalBillingAt appliedAt
      fulfillmentNote milestones milestonesFulfilledAt milestonesFulfillmentNote heldReason
    }
  }
`;
const REVIEW = gql`
  mutation ReviewReferralReward($id: String!, $approve: Boolean!, $note: String!) {
    adminReviewReferral(id: $id, approve: $approve, note: $note) { id status }
  }
`;
const FULFILL = gql`
  mutation FulfillReferralReward($id: String!, $action: String!, $note: String!) {
    adminFulfillReferral(id: $id, action: $action, note: $note) { id status }
  }
`;

interface Reward {
  id: string;
  friendId: string;
  friendName?: string;
  friendEmail?: string;
  invoiceId: string;
  friendSubscriptionId: string;
  activatedAt: number;
  eligibleAt: number;
  status: string;
  heldReason?: string | null;
  reviewedBy?: string;
  reviewedAt?: number;
  reviewNote?: string;
  issue?: string;
  targetBillingAt?: number;
  originalBillingAt?: number;
  appliedAt?: number;
  fulfillmentNote?: string;
  milestones: string[];
  milestonesFulfilledAt?: number;
  milestonesFulfillmentNote?: string;
}

const date = (value: number): string => new Date(value).toLocaleString();

function RewardRow({ reward, refresh }: { reward: Reward; refresh: () => Promise<unknown> }) {
  const [note, setNote] = useState("");
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");
  const [review, reviewState] = useMutation(REVIEW);
  const [fulfill, fulfillState] = useMutation(FULFILL);
  const busy = reviewState.loading || fulfillState.loading;
  const canReview = ["waiting", "review"].includes(reward.status);
  // Held with a reason (referrer paused, or another month still settling): the worker retries it.
  const retriesAutomatically = reward.status === "held" && !!reward.heldReason;
  const isEarlyReview = canReview && new Date(reward.eligibleAt) > new Date();
  const manualMilestones = reward.milestones.filter(
    (milestone) => milestone !== "brought_a_friend",
  );
  const submit = async (action: "approve" | "reject" | "retry" | "manual" | "milestones") => {
    setError("");
    try {
      if (action === "approve" || action === "reject") {
        await review({ variables: { id: reward.id, approve: action === "approve", note } });
      } else {
        await fulfill({ variables: { id: reward.id, action, note } });
      }
      await refresh();
      setNote("");
      setChecked(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to update referral");
    }
  };
  return (
    <Box py={2}>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Link href={`/users/${reward.friendId}`}>{reward.friendName ?? "Referred member"}</Link>
        <Chip
          size="small"
          label={
            reward.status === "payment"
              ? "Awaiting payment"
              : retriesAutomatically
                ? "held · retries automatically"
                : reward.status
          }
        />
      </Stack>
      <Typography variant="body2" mt={1}>
        Activated: {date(reward.activatedAt)}
      </Typography>
      {reward.status !== "payment" && (
        <Typography variant="body2">
          Slack notification scheduled: {date(reward.eligibleAt)}
        </Typography>
      )}
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        Invoice: {reward.invoiceId || "Awaiting first paid membership invoice"} · Subscription:{" "}
        {reward.friendSubscriptionId}
      </Typography>
      {reward.issue && (
        <Alert severity={retriesAutomatically ? "info" : "warning"} sx={{ mt: 1 }}>
          {reward.issue}
          {retriesAutomatically &&
            " No action needed: the worker retries this month automatically."}
        </Alert>
      )}
      {["approved", "applying"].includes(reward.status) && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Approval saved. The reward is still pending; the worker will retry automatically. You do
          not need to approve again.
        </Alert>
      )}
      {reward.targetBillingAt && (
        <Typography variant="body2" mt={1}>
          Saved renewal change: {reward.originalBillingAt && date(reward.originalBillingAt)} →{" "}
          {date(reward.targetBillingAt)}
        </Typography>
      )}
      {reward.reviewedAt && (
        <Typography variant="body2" mt={1}>
          Reviewed {date(reward.reviewedAt)} by {reward.reviewedBy}: {reward.reviewNote}
        </Typography>
      )}
      {reward.appliedAt && (
        <Typography variant="body2">
          Month fulfilled: {date(reward.appliedAt)}. {reward.fulfillmentNote}
        </Typography>
      )}
      {!!manualMilestones.length && (
        <Typography variant="body2" mt={1}>
          Milestones: {manualMilestones.join(", ")}.{" "}
          {reward.milestonesFulfilledAt
            ? `Fulfilled ${date(reward.milestonesFulfilledAt)}: ${reward.milestonesFulfillmentNote}`
            : "Support must arrange these rewards."}
        </Typography>
      )}
      {(canReview ||
        reward.status === "held" ||
        (!!manualMilestones.length && !reward.milestonesFulfilledAt)) && (
        <Stack gap={1} mt={2}>
          {isEarlyReview && (
            <Alert severity="info">
              The 60-hour waiting period has not ended. You can approve early after checking the
              payment and refund requests. Approval attempts to apply rewards immediately; Slack
              notifications will wait until the scheduled time.
            </Alert>
          )}
          {canReview && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(event) => setChecked(event.target.checked)}
                />
              }
              label="I checked Support's inbox/helpdesk: no refund request was made and this referral is eligible."
            />
          )}
          <TextField
            label="Review / fulfilment note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 2000 }}
            helperText="Record your check, rejection reason, or evidence of manual fulfilment."
          />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {canReview && (
              <>
                <Button
                  disabled={busy || !checked || note.trim().length < 5}
                  onClick={() => void submit("approve")}
                >
                  Approve + award one month
                </Button>
                <Button
                  color="error"
                  disabled={busy || note.trim().length < 5}
                  onClick={() => void submit("reject")}
                >
                  Reject referral
                </Button>
              </>
            )}
            {reward.status === "held" && (
              <>
                {!retriesAutomatically && (
                  <Button
                    disabled={busy || note.trim().length < 5}
                    onClick={() => void submit("retry")}
                  >
                    Retry automatic extension
                  </Button>
                )}
                <Button
                  disabled={busy || note.trim().length < 5}
                  onClick={() => void submit("manual")}
                >
                  I applied the month manually
                </Button>
              </>
            )}
            {!!manualMilestones.length && !reward.milestonesFulfilledAt && (
              <Button
                disabled={busy || note.trim().length < 5}
                onClick={() => void submit("milestones")}
              >
                Mark milestone rewards fulfilled
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default function ReferralRewardReview({ userId }: { userId: string }) {
  const { data, loading, error, refetch } = useQuery<{ adminReferralRewards: Reward[] }>(REWARDS, {
    variables: { userId },
    fetchPolicy: "network-only",
    pollInterval: 30000,
  });
  if (loading && !data) return <CircularProgress size={24} />;
  if (error)
    return <Alert severity="error">Unable to load referral reviews: {error.message}</Alert>;
  return (
    <Box mt={2}>
      <Typography variant="h6">Referral reviews and rewards</Typography>
      <Typography variant="body2" color="text.secondary">
        Slack notifications are sent after 60 hours. Support can review earlier once the friend's
        paid membership is confirmed. Approval immediately attempts to apply the month to an
        eligible recurring membership. If processing fails, the worker retries automatically.
        Historical referrals keep their existing counts and badges.
      </Typography>
      {!data?.adminReferralRewards.length && (
        <Typography mt={2}>No referrals in the new reward program.</Typography>
      )}
      {data?.adminReferralRewards.map((reward) => (
        <Box key={reward.id}>
          <RewardRow reward={reward} refresh={refetch} />
          <Divider />
        </Box>
      ))}
    </Box>
  );
}
